import { Link } from 'react-router-dom';
import type { TourEvent } from '@/features/events';
import { TOUR_HUB_PATH } from '@/features/events';
import { Section } from '@/components/layout';
import { ResponsiveImage } from '@/components/media';
import { Reveal } from '@/components/motion';
import { GoldLine } from '@/components/ornaments';
import { MEXICO_COPY } from '../copy';
import { ArrowRule } from '../ornaments/ArrowRule';
import styles from './CierreMexico.module.css';

const SOCIAL_ID = 'mexico-prueba-social';
const FAQ_ID = 'mexico-faq';

export interface CierreMexicoProps {
  event: TourEvent;
}

/**
 * S6 · PRUEBA SOCIAL · FAQ · UPSELL A CANCÚN · VUELTA A LA GIRA.
 *
 * ⚠️ ADICIÓN EXIGIDA POR EL BRIEF (ADR-006 · master §7 · `07_ARQUITECTURA_LANDINGS.md` §5-6).
 * Ninguno de los cuatro bloques está en `DEMO_4_Mexico.png`. Van juntos y
 * después del CTA de entradas, en el orden en que le sirven a quien llega por
 * un anuncio: primero la prueba de que esto ya ha pasado, luego sus dudas,
 * luego el techo del embudo, y por último la salida hacia el resto de la gira.
 *
 * LÍMITES DUROS que se respetan aquí:
 *  · La prueba social es **la única real** que existe (`comun/charla-miami-2023`).
 *    Sin cifras de asistentes, sin testimonios, sin "+500 almas" (ADR-003 §2).
 *  · Las preguntas y sus respuestas salen de `event.faq`. Lo que todavía no
 *    existe se responde diciendo que se anunciará: `isPending` lo marca en el
 *    dato y aquí se rotula, no se disimula.
 *
 * LA FAQ ES UN ACORDEÓN `<details>/<summary>` NATIVO — patrón unificado de las
 * seis páginas por decisión del ORACLE (25-jul-2026), estrenado en `/cancun`.
 * Se pliega la RESPUESTA, nunca la pregunta: la objeción que frena una compra
 * sigue a la vista y lo que se ahorra es el muro de texto. Por eso la marca
 * «Por anunciar» va en el `summary` — dentro del pliegue, lo que el ADR-003
 * obliga a declarar sólo existiría para quien ya hubiera abierto.
 *  · El upsell usa los datos REALES de Cancún leídos de su ficha, no un texto
 *    de venta escrito a mano.
 */
export function CierreMexico({ event }: CierreMexicoProps) {
  const { social, faq, volver } = MEXICO_COPY;

  return (
    <Section background="surface" spacing="tight" className={styles.section}>
      {/* ---------- Prueba social ---------- */}
      <Reveal className={styles.social} aria-labelledby={SOCIAL_ID}>
        <div className={styles.socialMedia}>
          <ResponsiveImage
            asset="comun/charla-miami-2023"
            sizes="(min-width: 64rem) 30vw, 86vw"
            className={styles.socialPhoto}
          />
        </div>

        <div className={styles.socialBody}>
          <h2 id={SOCIAL_ID} className={styles.eyebrow}>
            {social.eyebrow}
          </h2>
          <p className={styles.socialCaption}>{social.caption}</p>
          <GoldLine ornament="diamond" width="sm" className={styles.socialRule} />
        </div>
      </Reveal>

      {/* ---------- FAQ ---------- */}
      <Reveal className={styles.faq} aria-labelledby={FAQ_ID}>
        <h2 id={FAQ_ID} className={styles.title}>
          <ArrowRule className={styles.arrow} />
          <span>{faq.eyebrow}</span>
          <ArrowRule direction="left" className={styles.arrow} />
        </h2>

        <ul className={styles.faqList} role="list">
          {event.faq.map((item) => (
            <li key={item.id} className={styles.faqItem}>
              {/* `name` compartido = sólo una respuesta abierta a la vez. Es
               * comportamiento nativo del navegador; donde no esté soportado
               * degrada a acordeón normal, nunca a contenido inaccesible. */}
              <details className={styles.faqDetails} name={FAQ_ID}>
                <summary className={styles.faqSummary}>
                  <span className={styles.faqQuestion}>
                    {item.question}
                    {item.isPending ? <span className={styles.faqPending}>{faq.pendiente}</span> : null}
                  </span>
                  <span className={styles.faqMark} aria-hidden="true" />
                </summary>
                <p className={styles.faqAnswer}>{item.answer}</p>
              </details>
            </li>
          ))}
        </ul>
      </Reveal>

      {/* ---------- Upsell a Cancún: SE FUE DE AQUÍ (28-jul-2026) ----------
          Era el tercero de los cuatro bloques de esta banda: ceja, título de
          Cancún, meta y enlace, todo centrado y sin fotografía.

          El cliente revisó `/buenos-aires` en vídeo, señaló el bloque
          horizontal CON foto —«El nivel final del recorrido»— y pidió que fuera
          el de todas las ciudades. Entre esta versión, la de Bogotá y la de
          Miami había tres maneras distintas de contar el mismo escalón del
          embudo. Lo monta ahora `MexicoCityPage` con `CancunUpsell`, delante de
          esta banda, y de paso el bloque trae la línea de continuidad y su
          propio botón de reserva, que aquí no existían.

          `MEXICO_COPY.upsell` se queda en `copy.ts` sin usar a propósito: es
          copy aprobado y devolverlo es un import, no una redacción. */}

      {/* ---------- Vuelta a la gira ---------- */}
      <p className={styles.back}>
        <Link to={TOUR_HUB_PATH} className={styles.backLink}>
          <span aria-hidden="true">←</span>
          <span>
            {volver.label}
            <span className="az-visually-hidden"> (desde {event.city})</span>
          </span>
        </Link>
      </p>
    </Section>
  );
}
